import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { aepAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Loader2, Download, Plus, Trash2, Edit, ShieldCheck, Search,
  ExternalLink, Filter, RotateCcw, Link as LinkIcon, Check, Copy,
} from 'lucide-react';

const STATUS_CELL = {
  missing:    { bg: 'bg-red-100 text-red-700',       label: 'Missing' },
  in_process: { bg: 'bg-amber-100 text-amber-700',   label: 'In Process' },
  complete:   { bg: 'bg-emerald-100 text-emerald-700', label: 'Complete' },
  na:         { bg: 'bg-slate-100 text-slate-500',   label: 'N/A' },
};

export default function AepCertificationsAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('grid');

  if (!isKRRole(user)) return <Navigate to="/dashboard" replace />;

  return (
    <div data-testid="aep-admin-page">
      <PageHeader
        title="AEP 2027 Certification Tracker"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'AEP Certifications' },
        ]}
      />

      <div className="page-content">
        <ShareLinkCard />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="grid" data-testid="tab-aep-grid">
              <ShieldCheck size={14} className="mr-1" />
              Summary Grid
            </TabsTrigger>
            <TabsTrigger value="carriers" data-testid="tab-aep-carriers">
              <Edit size={14} className="mr-1" />
              Carrier Catalog
            </TabsTrigger>
          </TabsList>

          <TabsContent value="grid">
            <AepSummaryGrid />
          </TabsContent>
          <TabsContent value="carriers">
            <AepCarrierAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Summary Grid — agents × carriers matrix
// ─────────────────────────────────────────────────────────────
function AepSummaryGrid() {
  const [data, setData] = useState({ carriers: [], agents: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | with_missing | complete_only

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aepAPI.adminSummary();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load grid');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.agents || []).filter((a) => {
      if (q && !(
        (a.name || '').toLowerCase().includes(q) ||
        (a.npn || '').toLowerCase().includes(q) ||
        (a.agency_name || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q)
      )) return false;
      if (statusFilter === 'with_missing' && a.missing === 0) return false;
      if (statusFilter === 'complete_only' && a.complete !== (a.active_total ?? (data.carriers || []).length)) return false;
      return true;
    });
  }, [data, search, statusFilter]);

  const totals = useMemo(() => {
    const carriers = data.carriers || [];
    return carriers.map((c) => {
      let complete = 0, in_process = 0, missing = 0, na = 0;
      for (const a of data.agents || []) {
        const s = a.statuses?.[c.carrier_id] || 'missing';
        if (s === 'complete') complete++;
        else if (s === 'in_process') in_process++;
        else if (s === 'na') na++;
        else missing++;
      }
      return { carrier_id: c.carrier_id, complete, in_process, missing, na };
    });
  }, [data]);

  const handleDownloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(aepAPI.adminSummaryCsvUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aep-certifications.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download CSV');
    }
  };

  if (loading) {
    return <Card><CardContent className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <CardTitle className="text-lg font-heading">Agents × Carriers</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            {data.agents.length} agent{data.agents.length === 1 ? '' : 's'} • {data.carriers.length} active carrier{data.carriers.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search agent / NPN / agency…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 pl-7"
              data-testid="aep-grid-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="aep-grid-filter">
              <Filter size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              <SelectItem value="with_missing">With missing certs</SelectItem>
              <SelectItem value="complete_only">100% complete only</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="aep-grid-refresh">
            <RotateCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadCsv} data-testid="aep-grid-csv">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {filteredAgents.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No agents match. Agents appear here once they have an active Medicare contract
            or save at least one certification status.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-2 sticky left-0 bg-slate-50 z-10 min-w-[220px]">Agent</th>
                  <th className="text-left p-2 min-w-[140px]">Agency</th>
                  <th className="text-center p-2">Complete</th>
                  {data.carriers.map((c) => (
                    <th key={c.carrier_id} className="text-center p-2 min-w-[110px] text-xs font-medium text-slate-600">
                      {c.name}
                    </th>
                  ))}
                </tr>
                <tr className="border-b bg-slate-100 text-[10px] text-slate-500">
                  <th colSpan={3} className="p-1 text-right">Totals →</th>
                  {totals.map((t) => (
                    <th key={t.carrier_id} className="text-center p-1 font-normal">
                      <span className="text-emerald-600">{t.complete}</span>
                      <span className="text-slate-300"> / </span>
                      <span className="text-amber-600">{t.in_process}</span>
                      <span className="text-slate-300"> / </span>
                      <span className="text-red-600">{t.missing}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map((a) => {
                  // Backend now returns pct + active_total (excludes N/A). Fall
                  // back to old formula for safety if server hasn't been
                  // updated yet.
                  const activeTotal = a.active_total ?? data.carriers.length;
                  const pct = a.pct ?? (data.carriers.length ? Math.round((a.complete / data.carriers.length) * 100) : 0);
                  return (
                    <tr key={a.npn} className="border-b hover:bg-slate-50" data-testid={`aep-row-${a.npn}`}>
                      <td className="p-2 sticky left-0 bg-white z-10 min-w-[220px]">
                        <Link to={`/agents/${a.npn}?tab=aep-certifications`} className="text-sky-600 hover:underline font-medium">
                          {a.name || `NPN ${a.npn}`}
                        </Link>
                        <div className="text-[10px] text-slate-400">NPN {a.npn}</div>
                      </td>
                      <td className="p-2 text-xs text-slate-600">{a.agency_name || '—'}</td>
                      <td className="p-2 text-center">
                        <Badge className={pct === 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                          {a.complete}/{activeTotal} ({pct}%)
                        </Badge>
                      </td>
                      {data.carriers.map((c) => {
                        const s = a.statuses?.[c.carrier_id] || 'missing';
                        const meta = STATUS_CELL[s] || STATUS_CELL.missing;
                        return (
                          <td key={c.carrier_id} className="p-1 text-center">
                            <span className={`inline-block w-full py-1 px-1.5 rounded text-[11px] font-medium ${meta.bg}`}>
                              {meta.label}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Carrier Catalog Admin — add / edit / instructions / deactivate
// ─────────────────────────────────────────────────────────────
function AepCarrierAdmin() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // carrier obj or null
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aepAPI.adminListCarriers(true);
      setCarriers(res.data.carriers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load carriers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload, isNew) => {
    try {
      if (isNew) {
        await aepAPI.adminCreateCarrier(payload);
        toast.success('Carrier added');
      } else {
        await aepAPI.adminUpdateCarrier(editing.carrier_id, payload);
        toast.success('Carrier updated');
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDeactivate = async () => {
    try {
      await aepAPI.adminDeactivateCarrier(deleting.carrier_id);
      toast.success('Carrier deactivated');
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deactivate failed');
    }
  };

  if (loading) {
    return <Card><CardContent className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></CardContent></Card>;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-heading">Carrier Catalog</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Agents see only active carriers on their AEP Certifications tab.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="aep-add-carrier-btn">
            <Plus size={14} className="mr-1" /> Add Carrier
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-500 uppercase">
                  <th className="p-2">Name</th>
                  <th className="p-2">Portal URL</th>
                  <th className="p-2">Instructions</th>
                  <th className="p-2">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map((c) => (
                  <tr key={c.carrier_id} className="border-b hover:bg-slate-50" data-testid={`aep-carrier-${c.carrier_id}`}>
                    <td className="p-2 font-medium">{c.name}</td>
                    <td className="p-2 text-xs">
                      {c.portal_url ? (
                        <a href={c.portal_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline inline-flex items-center gap-1">
                          {c.portal_url.length > 40 ? c.portal_url.slice(0, 40) + '…' : c.portal_url}
                          <ExternalLink size={10} />
                        </a>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2 text-xs text-slate-500">
                      {c.instructions
                        ? (c.instructions.length > 60 ? c.instructions.slice(0, 60) + '…' : c.instructions)
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2">
                      {c.is_active
                        ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        : <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(c)}
                        data-testid={`aep-edit-${c.carrier_id}`}
                      >
                        <Edit size={14} />
                      </Button>
                      {c.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleting(c)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`aep-deactivate-${c.carrier_id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {carriers.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">No carriers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <CarrierDialog
        open={creating || !!editing}
        carrier={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSave={handleSave}
        onRefresh={load}
      />

      {/* Deactivate confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate carrier?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleting?.name}&quot; will be hidden from agents. Historical certifications are preserved. You can re-activate later by editing the carrier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700" data-testid="aep-confirm-deactivate">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CarrierDialog({ open, carrier, onClose, onSave, onRefresh }) {
  const isNew = !carrier;
  const [form, setForm] = useState({
    name: '', portal_url: '', instructions: '', is_active: true,
  });

  useEffect(() => {
    if (carrier) {
      setForm({
        name: carrier.name || '',
        portal_url: carrier.portal_url || '',
        instructions: carrier.instructions || '',
        is_active: carrier.is_active !== false,
      });
    } else {
      setForm({ name: '', portal_url: '', instructions: '', is_active: true });
    }
  }, [carrier, open]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const payload = {
      name: form.name.trim(),
      portal_url: form.portal_url.trim() || null,
      instructions: form.instructions.trim() || null,
      ...(isNew ? {} : { is_active: form.is_active }),
    };
    onSave(payload, isNew);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="aep-carrier-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Carrier' : `Edit ${carrier?.name}`}</DialogTitle>
          <DialogDescription>
            Instructions are shown to agents as a collapsible section on their AEP Certifications tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="aep-name">Name *</Label>
            <Input
              id="aep-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Humana"
              data-testid="aep-form-name"
            />
          </div>
          <div>
            <Label htmlFor="aep-url">Portal URL</Label>
            <Input
              id="aep-url"
              value={form.portal_url}
              onChange={(e) => setForm({ ...form, portal_url: e.target.value })}
              placeholder="https://…"
              data-testid="aep-form-url"
            />
          </div>
          <div>
            <Label htmlFor="aep-instructions">Instructions (agent-facing)</Label>
            <Textarea
              id="aep-instructions"
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              rows={6}
              placeholder="Plain text. Line breaks are preserved."
              data-testid="aep-form-instructions"
            />
          </div>
          {!isNew && (
            <CarrierLogoUpload carrier={carrier} onRefresh={onRefresh} />
          )}
          {!isNew && (
            <div className="flex items-center gap-2">
              <input
                id="aep-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="aep-form-active"
              />
              <Label htmlFor="aep-active" className="cursor-pointer">Active (visible to agents)</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="aep-form-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Share-link card — copyable /aep URL + WhatsApp/email-ready message
// ─────────────────────────────────────────────────────────────
function ShareLinkCard() {
  const url = `${window.location.origin}/aep`;
  const message = `Hi! Please update your AEP 2027 carrier certifications here: ${url}\n\nType your name, then enter your NPN as a PIN. Takes 30 seconds. — KR Agency Hub`;
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 1800);
    } catch (e) {
      toast.error('Copy failed — please copy manually');
    }
  };

  return (
    <Card className="mb-4 border-l-4 border-l-sky-500 bg-gradient-to-r from-sky-50 to-white" data-testid="aep-share-card">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <LinkIcon size={18} className="text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-slate-900">Share with agents</p>
              <p className="text-xs text-slate-500 mt-0.5">
                No login required — agents use their NPN as a PIN. Send this link over WhatsApp, SMS, or email.
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono text-slate-700 truncate max-w-full" data-testid="aep-share-url">
                  {url}
                </code>
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(url, setCopiedUrl)}
              data-testid="aep-copy-url-btn"
            >
              {copiedUrl ? <Check size={14} className="mr-1 text-emerald-600" /> : <Copy size={14} className="mr-1" />}
              {copiedUrl ? 'Copied' : 'Copy link'}
            </Button>
            <Button
              size="sm"
              onClick={() => copy(message, setCopiedMsg)}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="aep-copy-message-btn"
            >
              {copiedMsg ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
              {copiedMsg ? 'Copied' : 'Copy message'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



// ─────────────────────────────────────────────────────────────
// Phase 27.30.18 — Carrier logo uploader (inside CarrierDialog)
// Phase 27.30.26 — Fixed: logo upload used to piggy-back on `onSave`,
// which then fired a bogus `adminUpdateCarrier(id, null)` PUT and
// crashed the render tree. Logo upload now takes a dedicated `onRefresh`
// callback that only reloads the carrier list — no phantom save.
function CarrierLogoUpload({ carrier, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(carrier?.logo_url || null);

  useEffect(() => { setPreview(carrier?.logo_url || null); }, [carrier?.logo_url]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be ≤ 2 MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const r = await aepAPI.adminUploadCarrierLogo(carrier.carrier_id, file);
      setPreview(r.data.logo_url);
      toast.success('Logo uploaded');
      if (typeof onRefresh === 'function') onRefresh();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the current logo?')) return;
    setUploading(true);
    try {
      await aepAPI.adminDeleteCarrierLogo(carrier.carrier_id);
      setPreview(null);
      toast.success('Logo removed');
      if (typeof onRefresh === 'function') onRefresh();
    } catch {
      toast.error('Could not remove logo');
    } finally {
      setUploading(false);
    }
  };

  const url = preview
    ? (preview.startsWith('http') ? preview : `${process.env.REACT_APP_BACKEND_URL}${preview}`)
    : null;

  return (
    <div>
      <Label>Carrier logo</Label>
      <div className="mt-1 flex items-center gap-4">
        <div className="w-20 h-20 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 p-2.5">
          {url ? (
            <img src={url} alt="" className="w-full h-full object-contain" />
          ) : (
            <span className="text-slate-400 text-xs">No logo</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-white bg-[#0f2a5e] hover:bg-[#0a1f47] px-3 py-1.5 rounded"
                 data-testid="aep-form-logo-upload">
            {uploading ? 'Uploading…' : (url ? 'Replace logo' : 'Upload logo')}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                   className="hidden" onChange={onFile} disabled={uploading} />
          </label>
          {url && (
            <button
              type="button"
              onClick={removeLogo}
              disabled={uploading}
              className="text-xs text-rose-600 hover:text-rose-700 underline block"
              data-testid="aep-form-logo-remove"
            >
              Remove logo
            </button>
          )}
          <p className="text-[10px] text-slate-400">
            PNG, JPG, WebP or SVG. Max 2 MB. Square or wide logos look best.
          </p>
        </div>
      </div>
    </div>
  );
}
